import React from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import {
  brandColours,
  fontSize,
  fontWeights,
  minBreakpointQuery,
  sectionMargins,
  standardColours,
} from '../styles';
import { Button, Container } from './ui';
import tickIcon from '../images/featured-resource-tick.svg';

const StyledFeaturedResourceCta = styled.article`
  ${sectionMargins('20px', '40px')};
`;

const StyledContainer = styled(Container)`
  padding-left: 0;
  padding-right: 0;
`;

const StyledInner = styled.div`
  background-color: ${brandColours.octonary};
  border-radius: 12px;
  padding: 20px;

  ${minBreakpointQuery.small`
    padding: 25px;
  `}

  ${minBreakpointQuery.smedium`
    padding: 35px;
  `}

  ${minBreakpointQuery.mlarge`
    padding: 45px;
  `}
`;

const StyledContent = styled.div`
  display: grid;
  background-color: ${standardColours.white};
  border-radius: 12px;
  padding: 15px;
  gap: 20px;

  ${minBreakpointQuery.small`
    gap: 25px;
    padding: 20px;
  `}

  ${minBreakpointQuery.smedium`
    grid-template-columns: repeat(2, 1fr);
    padding: 25px;
  `}

  ${minBreakpointQuery.mlarge`
    padding: 30px;
    grid-template-columns: .66fr .34fr;
    gap: 35px;
  `}
`;

const StyledHeader = styled.header`
  max-width: 760px;

  ${minBreakpointQuery.smedium`
    grid-column: span 2;
  `}
`;

const StyledHeading = styled.h2`
  color: ${brandColours.secondary};
  ${fontSize(20)};
  font-weight: ${fontWeights.semibold};
  margin-bottom: 20px;

  ${minBreakpointQuery.small`
    ${fontSize(22)};
    margin-bottom: 25px;
  `}

  ${minBreakpointQuery.smedium`
    margin-bottom: 30px;
  `}

  ${minBreakpointQuery.mlarge`
    ${fontSize(24)};
    margin-bottom: 35px;
  `}
`;

const StyledText = styled.p`
  line-height: 1.4;

  ${minBreakpointQuery.small`
    ${fontSize(18)};
  `}
`;

const StyledKeyPoints = styled.ul`
  display: grid;
  gap: 10px;

  ${minBreakpointQuery.small`
    grid-template-columns: repeat(2, 1fr);
  `}

  ${minBreakpointQuery.smedium`
    gap: 15px;
    grid-template-columns: 1fr;
  `}
  
  ${minBreakpointQuery.mlarge`
    gap: 20px;
    grid-template-columns: repeat(2, 1fr);
  `}
`;

const StyledKeyPoint = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;

  ${minBreakpointQuery.small`
    ${fontSize(18)};
  `}

  &:before {
    content: '';
    background: url(${tickIcon}) no-repeat;
    display: inline-block;
    width: 18px;
    height: 18px;
    background-size: 100%;
    flex-shrink: 0;

    ${minBreakpointQuery.small`
      width: 22px;
      height: 22px;
    `}
  }
`;

const StyledImage = styled(GatsbyImage)`
  margin-top: auto;
  width: 100%;

  ${minBreakpointQuery.smedium`
    grid-area: 2 / 2 / 4 / 2;
  `}
`;

const StyledButton = styled(Button)`
  color: ${brandColours.secondary};
  border-radius: 45px;
  ${fontSize(12)};

  ${minBreakpointQuery.mlarge`
    ${fontSize(14)};
  `};
`;

const FeaturedResourceCta = ({
  link,
  ctaHeading,
  ctaText,
  ctaImage,
  ctaKeyPoints,
}) => {
  const keyPoints = JSON.parse(ctaKeyPoints);

  return (
    <StyledFeaturedResourceCta>
      <StyledContainer narrower={true}>
        <StyledInner>
          <StyledContent>
            <StyledHeader>
              <StyledHeading>{ctaHeading}</StyledHeading>
              <StyledText>{ctaText}</StyledText>
            </StyledHeader>
            <StyledKeyPoints>
              {keyPoints.map((keyPoint, id) => (
                <StyledKeyPoint key={id}>{keyPoint}</StyledKeyPoint>
              ))}
            </StyledKeyPoints>
            <StyledButton to={link.pageUrl}>{link.text}</StyledButton>
            <StyledImage
              image={ctaImage.gatsbyImageData}
              objectFit="contain"
              alt={ctaImage.alt}
            />
          </StyledContent>
        </StyledInner>
      </StyledContainer>
    </StyledFeaturedResourceCta>
  );
};

export default FeaturedResourceCta;
